import { useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';
import { z } from 'zod';
import { Url } from '@rossum/api-client';
import { User } from '@rossum/api-client/users';
import { IconUserPlus } from '@rossum/ui/icons/tabler';
import {
  Button,
  CircularProgress,
  DialogContent,
  FormLabel,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { useCreateUser } from '../../../../business/users/useCreateUser';
import SingleCheckboxControl from '../../../../components/ReactHookForm/controls/SingleCheckboxControl';
import TextFieldControl from '../../../../components/ReactHookForm/controls/TextFieldControl';
import { getRoleOptions } from '../../../../redux/modules/groups/helpers';
import { throwInfo } from '../../../../redux/modules/messages/actions';
import { isSSOEnable } from '../../../../redux/modules/organization/selectors';
import { splitFullName } from '../../../../redux/modules/user/helpers';
import { State } from '../../../../types/state';
import { approvalWorkflowsFeatureSelector } from '../../../pricing/selectors';
import { useWorkspacesWithQueues } from '../../../queues/hooks/useWorkspacesWithQueues';
import { userDetailsAccessPath } from '../../routes';
import { RoleSelect } from '../RoleSelect';
import { RoleTooltip } from '../RoleTooltip';
import { UserQueueSelect } from './UserQueueSelect';

type AddUserDialogContentProps = {
  onCancel: () => void;
};

type AddUserFormState = {
  name: string;
  email: string;
  role: string;
  canApproveCheckbox: boolean;
  queues: number[];
};

export const AddUserDialogContent = ({
  onCancel,
}: AddUserDialogContentProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const enableApproverRole = useSelector(approvalWorkflowsFeatureSelector);
  const isSSO = useSelector(isSSOEnable);
  const [selectedQueues, setSelectedQueues] = useState<number[]>([]);

  const { workspacesWithQueues } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const selectedQueueUrls = useMemo<Url[]>(() => {
    if (!workspacesWithQueues) return [];

    const queueIdToUrlMap = new Map(
      workspacesWithQueues.flatMap(ws => ws.queues.map(q => [q.id, q.url]))
    );

    return selectedQueues
      .map(id => queueIdToUrlMap.get(id))
      .filter((url): url is Url => url !== undefined);
  }, [selectedQueues, workspacesWithQueues]);

  const roleOptions = useSelector((state: State) =>
    getRoleOptions(state.groups, intl)
  );

  const roleOptionsWithFlag = roleOptions.filter(role =>
    enableApproverRole ? role : role.name !== 'approver'
  );

  const defaultValues: AddUserFormState = {
    name: '',
    email: '',
    role: '',
    canApproveCheckbox: false,
    queues: [],
  };

  const addUserValidationFormSchema = z.object({
    name: z.string().min(
      1,
      intl.formatMessage({
        id: 'containers.settings.users.addUser.name.error',
      })
    ),
    email: z
      .string()
      .min(
        1,
        intl.formatMessage({
          id: 'containers.settings.users.addUser.email.required',
        })
      )
      .email(
        intl.formatMessage({
          id: 'containers.settings.users.addUser.email.error',
        })
      ),
    role: z.string().min(
      1,
      intl.formatMessage({
        id: 'containers.settings.users.addUser.role.error',
      })
    ),
    canApproveCheckbox: z.boolean().optional(),
    queues: z.array(z.number()).optional(),
  });

  const { handleSubmit, control, setError, watch } = useForm({
    mode: 'onSubmit',
    defaultValues,
    resolver: zodResolver(addUserValidationFormSchema),
  });

  const watchValues = watch();

  const handleOnSuccess = (user: User) => {
    // SSO users don't receive the password setting email.
    dispatch(throwInfo(isSSO ? 'userCreatedSso' : 'userCreated'));
    dispatch(push(userDetailsAccessPath(user.id)));
  };

  const { mutate, isLoading, error, apiErrorMessage } =
    useCreateUser(handleOnSuccess);

  useEffect(() => {
    if (error || apiErrorMessage) {
      const message =
        apiErrorMessage ??
        intl.formatMessage({
          id: 'containers.settings.users.addUser.email.apiError',
        });

      setError('email', { message });
    }
  }, [error, apiErrorMessage, intl, setError]);

  return (
    <DialogContent sx={{ px: 6, my: 4, pb: 4 }}>
      <form
        onSubmit={handleSubmit(({ name, email, role, canApproveCheckbox }) => {
          const { firstName, lastName } = splitFullName(name);

          return mutate({
            firstName,
            lastName,
            email,
            role: canApproveCheckbox
              ? [
                  get(
                    roleOptions.find(option => option.name === role),
                    'value',
                    ''
                  ),
                  get(
                    roleOptions.find(option => option.name === 'approver'),
                    'value',
                    ''
                  ),
                ]
              : [
                  get(
                    roleOptions.find(option => option.name === role),
                    'value',
                    ''
                  ),
                ],

            queues: selectedQueueUrls,
          });
        })}
      >
        <Stack spacing={2}>
          <Stack spacing={2}>
            <TextFieldControl
              type="string"
              ControllerProps={{ control, name: 'name' }}
              label={intl.formatMessage({
                id: 'containers.settings.users.addUser.name.label',
              })}
              placeholder={intl.formatMessage({
                id: 'containers.settings.users.addUser.name.placeholder',
              })}
              inputProps={{
                'data-cy': 'add-user-modal-name-input',
              }}
            />

            <TextFieldControl
              type="string"
              ControllerProps={{ control, name: 'email' }}
              label={intl.formatMessage({
                id: 'containers.settings.users.addUser.email.label',
              })}
              placeholder={intl.formatMessage({
                id: 'containers.settings.users.addUser.email.placeholder',
              })}
              inputProps={{
                'data-cy': 'add-user-modal-email-input',
              }}
            />

            <Stack>
              <FormLabel sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography color="text.primary" sx={{ mr: 1 }}>
                  {intl.formatMessage({
                    id: 'containers.settings.users.addUser.queues.label',
                  })}
                </Typography>
              </FormLabel>

              <Controller
                control={control}
                name="queues"
                render={({ field }) => (
                  <UserQueueSelect
                    data-cy="add-user-modal-queues-select"
                    selectedQueues={field.value ?? []}
                    setSelectedQueues={queues => {
                      field.onChange(queues);
                      setSelectedQueues(queues);
                    }}
                  />
                )}
              />
            </Stack>
            <Stack>
              <Stack>
                <FormLabel sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography color="text.primary" sx={{ mr: 1 }}>
                    {intl.formatMessage({
                      id: 'containers.settings.users.addUser.role.label',
                    })}
                  </Typography>
                  <RoleTooltip
                    roles={roleOptionsWithFlag.map(({ name }) => name)}
                  />
                </FormLabel>
              </Stack>
              <RoleSelect<AddUserFormState>
                name="role"
                control={control}
                roleOptions={roleOptionsWithFlag}
                getErrorMessage={(_, fieldError) => fieldError.message || ''}
              />
            </Stack>

            <Stack sx={{ pl: 1 }}>
              {!['admin', 'approver'].includes(watchValues.role) &&
                enableApproverRole && (
                  <SingleCheckboxControl
                    ControllerProps={{ control, name: 'canApproveCheckbox' }}
                    FieldLabelProps={{ layout: 'none' }}
                    label={intl.formatMessage({
                      id: 'containers.settings.users.addUser.canApprove',
                    })}
                    data-cy="add-user-modal-can-approve-checkbox"
                  />
                )}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => onCancel()}
              color="secondary"
              data-cy="add-user-modal-cancel-button"
            >
              {intl.formatMessage({
                id: 'containers.settings.users.addUser.cancel',
              })}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={
                isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <SvgIcon fontSize="large">
                    <IconUserPlus />
                  </SvgIcon>
                )
              }
              data-cy="add-user-modal-submit-button"
            >
              {intl.formatMessage({
                id: 'containers.settings.sidebar.users.addUser',
              })}
            </Button>
          </Stack>
        </Stack>
      </form>
    </DialogContent>
  );
};
